import { Injectable } from '@angular/core';

import * as Sentry from '@sentry/angular';

export interface SentryLogContext {
    [key: string]: any;
}

export interface SentryLogOptions {
    component?: string;
    feature?: string;
    tags?: Record<string, string>;
    level?: Sentry.SeverityLevel;
}

@Injectable({
    providedIn: 'root'
})
export class SentryLoggerService {

    /**
     * Logs a message to Sentry with optional context
     * @param message - The message to log
     * @param context - Optional context data to include
     * @param options - Optional configuration (component, feature, tags, level)
     */
    log(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        const level = options?.level || 'info';

        // Log to console using Sentry.logger
        // Note: We don't include 'options' in logData to avoid Sentry data scrubbing rules
        // The options are already properly set via scope.setTag() and scope.setExtra() below
        const logData = { message, context, metadata: options };
        switch (level) {
            case 'debug':
                Sentry.logger.debug(message, logData);
                break;
            case 'info':
                Sentry.logger.info(message, logData);
                break;
            case 'warning':
                Sentry.logger.warn(message, logData);
                break;
            case 'error':
                Sentry.logger.error(message, logData);
                break;
            case 'fatal':
                Sentry.logger.error(message, logData);
                break;
            default:
                Sentry.logger.info(message, logData);
        }

        // Send to Sentry
        Sentry.withScope(scope => {
            if (options?.component) {
                scope.setTag('component', options.component);
            }
            if (options?.feature) {
                scope.setTag('feature', options.feature);
            }
            if (options?.tags) {
                Object.keys(options.tags).forEach(key => {
                    scope.setTag(key, options.tags![key]);
                });
            }

            scope.setLevel(level);
            scope.setExtra('message', message);

            // Use setContext instead of setExtra to avoid data scrubbing rules
            // Contexts are less likely to be filtered by Sentry's scrubbing rules
            if (context && Object.keys(context).length > 0) {
                scope.setContext('logContext', context);
            }

            // Use captureMessage which respects the scope
            Sentry.captureMessage(message, level);
        });
    }

    /**
     * Logs an error/exception to Sentry with optional context
     * @param error - The error or error message
     * @param context - Optional context data to include
     * @param options - Optional configuration (component, feature, tags, level)
     */
    logError(error: Error | string, context?: SentryLogContext, options?: SentryLogOptions): void {
        const level = options?.level || 'error';
        // Guard against nullish/non-Error values reaching this point (e.g. `throwError(() => undefined)`),
        // which would otherwise crash on `errorObj.message` below.
        const errorObj = typeof error === 'string'
            ? new Error(error)
            : (error instanceof Error ? error : new Error(error != null ? String(error) : 'Unknown error'));

        // Log to console using Sentry.logger
        // Note: We don't include 'options' in logData to avoid Sentry data scrubbing rules
        // The options are already properly set via scope.setTag() and scope.setExtra() below
        const logData = { error: errorObj, context, metadata: options };
        Sentry.logger.error(errorObj.message, logData);

        // Send to Sentry
        Sentry.withScope(scope => {
            if (options?.component) {
                scope.setTag('component', options.component);
            }
            if (options?.feature) {
                scope.setTag('feature', options.feature);
            }
            if (options?.tags) {
                Object.keys(options.tags).forEach(key => {
                    scope.setTag(key, options.tags![key]);
                });
            }

            scope.setLevel(level);
            scope.setExtra('message', errorObj.message);

            // Use setContext instead of setExtra to avoid data scrubbing rules
            // Contexts are less likely to be filtered by Sentry's scrubbing rules
            if (context && Object.keys(context).length > 0) {
                scope.setContext('logContext', context);
            }

            if (context?.errorStack) {
                errorObj.stack = context.errorStack;
            }

            Sentry.captureException(errorObj);
        });
    }

    /**
     * Convenience method for logging info level messages
     */
    info(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.log(message, context, { ...options, level: 'info' });
    }

    /**
     * Convenience method for logging warning level messages
     */
    warn(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.log(message, context, { ...options, level: 'warning' });
    }

    /**
     * Convenience method for logging error level messages
     */
    error(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.log(message, context, { ...options, level: 'error' });
    }

    /**
     * Convenience method for logging fatal level messages
     */
    fatal(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.log(message, context, { ...options, level: 'fatal' });
    }

    /**
     * Convenience method for logging debug level messages
     */
    debug(message: string, context?: SentryLogContext, options?: SentryLogOptions): void {
        this.log(message, context, { ...options, level: 'debug' });
    }
}

